import { type FC } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { getRivalriesForCountry } from '@/services/country-conflicts';

// ---------------------------------------------------------------------------
// Country Profile Card — mini floating card (Step 1 of Option A flow)
// Appears at bottom-left when user clicks a country capital on the map
// ---------------------------------------------------------------------------

const STANCE_BADGE: Record<string, { label: string; color: string }> = {
  western:     { label: 'Western',    color: 'text-sky-400 bg-sky-950/60 border-sky-800' },
  eastern:     { label: 'Eastern',    color: 'text-red-400 bg-red-950/60 border-red-800' },
  neutral:     { label: 'Neutral',    color: 'text-slate-400 bg-slate-800/60 border-slate-700' },
  revisionist: { label: 'Revisionist',color: 'text-orange-400 bg-orange-950/60 border-orange-800' },
  rogue:       { label: 'Rogue',      color: 'text-red-500 bg-red-950/60 border-red-700' },
  pivotal:     { label: 'Pivotal',    color: 'text-purple-400 bg-purple-950/60 border-purple-800' },
};

const RISK_COLOR: Record<string, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-400',
  medium:   'bg-yellow-400',
  low:      'bg-emerald-400',
};

export const CountryProfileCard: FC = () => {
  const selectedCountry = useAppStore((s) => s.selectedCountry);
  const clearCompare = useAppStore((s) => s.clearCompare);
  const setCompareMode = useAppStore((s) => s.setCompareMode);
  const compareMode = useAppStore((s) => s.compareMode);

  if (!selectedCountry) return null;

  const c = selectedCountry;
  const stance = STANCE_BADGE[c.geopoliticalStance] ?? { label: c.geopoliticalStance, color: 'text-slate-400 bg-slate-800 border-slate-700' };
  const rivalries = getRivalriesForCountry(c.code);
  const activeRivalries = rivalries.filter((r) => r.intensity === 'critical' || r.intensity === 'high').length;

  const handleCompare = () => {
    setCompareMode(true);
    // Keep selectedCountry, waiting for second click
  };

  return (
    <div
      className="fixed left-3 right-3 bottom-16 z-50 animate-slide-up md:absolute md:left-52 md:right-auto md:bottom-20 md:w-72"
      style={{ animationDuration: '150ms' }}
    >
      <div className="bg-gray-950/95 backdrop-blur-md border border-slate-700/60 rounded-xl shadow-2xl overflow-y-auto max-h-[calc(100svh-8rem)] md:max-h-[75vh]">

        {/* Header */}
        <div className="flex items-start justify-between p-3 border-b border-slate-800/60">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{c.flagEmoji}</span>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-[13px] font-bold text-slate-100">{c.name}</span>
                {c.source === 'rest-countries' && (
                  <span className="text-[7px] px-1 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700">BASIC</span>
                )}
              </div>
              <div className="text-[10px] text-slate-500">{c.capitalCity} · {c.code}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {activeRivalries > 0 && (
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-950/70 text-red-400 border border-red-900/50 font-bold">
                {activeRivalries} RIVALS
              </span>
            )}
            <button
              onClick={clearCompare}
              className="text-slate-500 hover:text-slate-300 text-xs px-1.5 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Power Index bar */}
        <div className="px-3 pt-2 pb-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] text-slate-500 uppercase tracking-wide">Global Power Index</span>
            <span className={clsx('text-[11px] font-bold',
              c.powerIndex >= 80 ? 'text-red-400' : c.powerIndex >= 60 ? 'text-orange-400' : 'text-yellow-400',
            )}>{c.powerIndex}/100</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all',
                c.powerIndex >= 80 ? 'bg-red-500' : c.powerIndex >= 60 ? 'bg-orange-400' : 'bg-yellow-400',
              )}
              style={{ width: `${c.powerIndex}%` }}
            />
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-0 px-2 py-1.5">
          {[
            { label: 'Population', value: `${c.population >= 1000 ? `${(c.population/1000).toFixed(1)}B` : `${c.population}M`}` },
            { label: 'GDP', value: `$${c.gdpBillionUSD >= 1000 ? `${(c.gdpBillionUSD/1000).toFixed(1)}T` : `${c.gdpBillionUSD}B`}` },
            { label: 'GDP/Cap', value: `$${c.gdpPerCapita >= 1000 ? `${(c.gdpPerCapita/1000).toFixed(0)}K` : c.gdpPerCapita}` },
          ].map((s) => (
            <div key={s.label} className="text-center p-1">
              <div className="text-[11px] font-semibold text-slate-200">{s.value}</div>
              <div className="text-[8px] text-slate-600">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Leader + stance row */}
        <div className="px-3 py-1.5 border-t border-slate-800/40 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-1">
            {c.leader.name === '—' ? (
              <span className="text-[10px] text-slate-600 italic">Intel limited</span>
            ) : (
              <>
                <span className={clsx('w-2 h-2 rounded-full shrink-0', RISK_COLOR[c.leader.riskLevel])} />
                <span className="text-[10px] text-slate-300">{c.leader.name}</span>
                <span className="text-[9px] text-slate-600">{c.leader.title}</span>
              </>
            )}
          </div>
          <span className={clsx('text-[8px] px-1.5 py-0.5 rounded border font-medium', stance.color)}>
            {stance.label}
          </span>
        </div>

        {/* Alliances */}
        {c.alliances.length > 0 && (
          <div className="px-3 py-1 flex flex-wrap gap-1">
            {c.alliances.slice(0, 5).map((al) => (
              <span key={al} className="text-[8px] px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-400 border border-slate-700/40">
                {al}
              </span>
            ))}
            {c.alliances.length > 5 && (
              <span className="text-[8px] text-slate-600">+{c.alliances.length - 5}</span>
            )}
          </div>
        )}

        {/* Basic metadata (REST Countries only) */}
        {c.source === 'rest-countries' && (c._region || c._languages || c._area) && (
          <div className="px-3 py-1.5 border-t border-slate-800/40 space-y-0.5">
            {c._region && (
              <div className="flex items-start gap-1.5">
                <span className="text-[8px] text-slate-600 w-14 shrink-0">Region</span>
                <span className="text-[9px] text-slate-400">{c._region}</span>
              </div>
            )}
            {c._languages && (
              <div className="flex items-start gap-1.5">
                <span className="text-[8px] text-slate-600 w-14 shrink-0">Languages</span>
                <span className="text-[9px] text-slate-400">{c._languages}</span>
              </div>
            )}
            {c._currencies && (
              <div className="flex items-start gap-1.5">
                <span className="text-[8px] text-slate-600 w-14 shrink-0">Currency</span>
                <span className="text-[9px] text-slate-400">{c._currencies}</span>
              </div>
            )}
            {c._area != null && c._area > 0 && (
              <div className="flex items-start gap-1.5">
                <span className="text-[8px] text-slate-600 w-14 shrink-0">Area</span>
                <span className="text-[9px] text-slate-400">{c._area.toLocaleString()} km²</span>
              </div>
            )}
          </div>
        )}

        {/* Military summary */}
        <div className="px-3 py-1.5 border-t border-slate-800/40">
          {c.military.activeTroops === 0 && c.military.combatAircraft === 0 && c.military.submarines === 0 ? (
            <div className="text-center py-1 text-[9px] text-slate-600 italic">Military data not publicly tracked</div>
          ) : (
            <div className="grid grid-cols-4 gap-1 text-center">
              {[
                { label: 'Aircraft', val: c.military.combatAircraft.toLocaleString() },
                { label: 'Carriers', val: c.military.aircraftCarriers },
                { label: 'Subs', val: c.military.submarines },
                { label: 'Troops', val: c.military.activeTroops >= 1000 ? `${(c.military.activeTroops/1000).toFixed(1)}M` : `${c.military.activeTroops}K` },
              ].map((m) => (
                <div key={m.label}>
                  <div className="text-[10px] font-semibold text-slate-300">{m.val}</div>
                  <div className="text-[8px] text-slate-600">{m.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 p-3 pt-2 border-t border-slate-800/40">
          <button
            onClick={handleCompare}
            disabled={compareMode}
            className={clsx(
              'flex-1 py-1.5 rounded text-[10px] font-semibold transition-colors border',
              compareMode
                ? 'bg-sky-900/60 text-sky-400 border-sky-700 animate-pulse'
                : 'bg-sky-900/40 text-sky-300 border-sky-800 hover:bg-sky-800/50',
            )}
          >
            {compareMode ? '⊕ Select target...' : '⚖ Compare vs...'}
          </button>
        </div>
      </div>
    </div>
  );
};
